<template>
  <RootPageLayout class="help" title="Support Request" mobileTitle="Support">
    <div v-if="isOffline()" class="layout__wrapper-inner container">
      <ContentWrapper>
        <p>
          It looks like you’re offline. Please connect to the Internet and try
          again.
        </p>
        <p>
          Alternatively, you can send us an email directly at
          <a href="mailto:help@shortform.com">help@shortform.com</a>.
        </p>
      </ContentWrapper>
    </div>

    <div v-else class="layout__wrapper-inner container">
      <ContentWrapper>
        <div class="help__subtitle">
          Fill out the form below, or send us an email directly at
          <a href="mailto:help@shortform.com">help@shortform.com</a>
        </div>
        <div v-show="sentAlert" class="alert alert-success">
          Successfully sent a request.
        </div>
        <form role="form" class="form" novalidate @submit.prevent="send">
          <fieldset id="support-edit-form-group-subject" class="form__group">
            <div tabindex="-1" role="group">
              <label class="form__label form__label--large">
                To get more help, write a descriptive subject
              </label>
              <InputWithContextMenu
                id="support-edit-subject"
                ref="subjectInput"
                v-model="subject"
                placeholder="Enter subject here"
                type="text"
                :class="validation.stateClass('subject')"
                class="form-control"
              />
              <FormError :errors="validation.errors" field="subject" />
            </div>
          </fieldset>
          <fieldset id="support-edit-form-group-body" class="form__group">
            <div tabindex="-1" role="group">
              <label class="form__label form__label--large">Request</label>
              <InputWithContextMenu
                id="support-edit-body"
                ref="bodyTextarea"
                v-model="body"
                elementType="textarea"
                placeholder="How can we help?"
                rows="8"
                type="text"
                :class="validation.stateClass('body')"
                class="form-control"
              />
              <FormError :errors="validation.errors" field="body" />
            </div>
          </fieldset>
          <div
            v-if="validation.errors && validation.errors._schema"
            class="invalid-feedback error-body"
          >
            {validation.errors._schema}
          </div>
          <button v-wait:disabled="'Sending'" type="submit" class="btns">
            <v-wait for="Sending">
              <template v-slot:waiting>
                Sending
                <button-spinner />
              </template>
              Send request
            </v-wait>
          </button>
          <a class="help__version" href="javascript:window.location.reload()"
            >v {{ APP_VERSION }}</a
          >
        </form>
      </ContentWrapper>
    </div>
  </RootPageLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import RootPageLayout from '@/app/components/RootPageLayout.vue'
import ContentWrapper from '@/components/ui/ContentWrapper.vue'
import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'

import { APP_VERSION } from '@/init/settings'
import { ValidationState, setInputFocusStart } from '@/helpers/form'
import { offline } from '@/services/offline'
import { backend } from '@/services/backend'
import { platform } from '@/services/platform'
import { processDevCommands } from '@/services/commands'

const router = useRouter()
const wait = useWait()

const subjectInput = ref<InstanceType<typeof InputWithContextMenu> | null>(null)

const validation = ref(new ValidationState())
const redirectDelay = ref(2000)

const sentAlert = ref(false)
const subject = ref('')
const body = ref('')

onMounted(async () => {
  if (offline.isOnline) {
    const input = subjectInput.value?.$el
    setInputFocusStart(input)

    const info = await platform.deviceInfo()
    if (window.__UNIT_TESTING !== true) {
      console.log(info)
    }
  }
  processDevCommands()
})

async function send(): Promise<void> {
  try {
    wait.start('Sending')
    await backend.sendSupportRequest(subject.value, body.value)
    validation.value.reset()

    // Show subscribed alert and redirect to books page after delay.
    sentAlert.value = true

    setTimeout(() => {
      wait.end('Sending')
      router.push({ name: 'home' })
    }, redirectDelay.value)
  } catch (error) {
    validation.value.showErrors(error)
    wait.end('Sending')
  }
}

function isOffline(): boolean {
  return offline.isOffline
}
</script>

<style lang="scss" scoped>
.help {
  &__subtitle {
    font-size: 18px;
    color: $c-black-3;
    a {
      color: $c-blue-3;
    }
  }
  &__version {
    margin-top: 1rem;
    float: right;
  }
}
</style>
